import { formatNumber } from '../../utils/numberFormat';
import { signalLabel } from './labels';

export interface LedgerBridgeInput {
  last30d: number;
  prospectActions30d: number;
  customerActions30d?: number;
  otherSignals30d?: { count: number; types: Array<{ type: string; count: number }> };
}

/**
 * The 30-day bridge on Data health: total = prospect actions + customer
 * logins and usage + rows that are nobody's action (our own sends, ad
 * impressions, enrichment). Null when the parts already add up. When the
 * server names the remainder, its types are listed; when it does not (an
 * older build) or the parts disagree, the gap is said plainly, never hidden.
 */
export function ledgerBridge(l: LedgerBridgeInput): { text: string; reconciles: boolean } | null {
  if (l.customerActions30d === undefined) return null;
  const known = l.prospectActions30d + l.customerActions30d;
  const gap = l.last30d - known;
  if (gap === 0) return null;
  const head = `${formatNumber(l.last30d)} signals in 30 days = ${formatNumber(l.prospectActions30d)} prospect actions + ${formatNumber(l.customerActions30d)} customer logins and usage`;
  const other = l.otherSignals30d;
  if (other && other.count === gap) {
    const top = other.types.slice(0, 3).map((t) => `${signalLabel(t.type)} ${formatNumber(t.count)}`);
    const rest = other.types.length > 3 ? `, ${other.types.length - 3} more types` : '';
    return { text: `${head} + ${formatNumber(gap)} not anyone’s action (${top.join(', ')}${rest})`, reconciles: true };
  }
  return {
    text: `${head} leaves ${formatNumber(gap)} unexplained${other ? ` (the server names ${formatNumber(other.count)})` : ''}`,
    reconciles: false,
  };
}

/**
 * Why Data health's "Demo booked" (22 in 30 days on 12 Oct 2026) is not
 * Channels' bookings (21): two definitions, both right. The ledger writes one
 * signal per ACTIVE Calendly booking on a known company, any meeting type,
 * each rebooking apart, at the moment it was booked. Channels counts Sales ›
 * Demo bookings: demo meetings only, a repeat within 30 days merged, cancelled
 * and unmatched bookings kept.
 *
 * One number here (review #18): the ledger's own; Channels' count is a
 * different definition and is not repeated. Null when the ledger shows no demo bookings.
 */
export const DEMO_BOOKED_DEFINITIONS =
  'The ledger writes one signal per active Calendly booking on a known company, any meeting type, each rebooking apart, when it was booked. Channels counts Sales’ demo bookings: demo meetings only, a repeat within 30 days merged, cancelled and unmatched bookings kept.';

export function demoBookedNote(
  byChannel: ReadonlyArray<{ types?: ReadonlyArray<{ type: string; count: number }> }>,
): string | null {
  const count = byChannel.flatMap((c) => c.types ?? []).find((t) => t.type === 'demo_booked')?.count;
  if (!count) return null;
  // One number only: Channels' own count is a different definition and lives on Channels.
  return `${signalLabel('demo_booked')}: ${formatNumber(count)} Calendly bookings on known companies in 30 days, any meeting type (Channels counts demo meetings only, so its total is lower)`;
}
